import { useState } from "react";
import { Box, Typography, Card, CardContent, TextField, Button, Snackbar, Alert, InputAdornment, IconButton } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Formik } from "formik";
import { useNavigate, useSearchParams } from "react-router-dom";
import { APIURL } from "../../globals.jsx";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const email = searchParams.get("email") || "";
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [done, setDone] = useState(false);

  if (!token || !email) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <Typography color="error">Invalid or missing reset link. Please request a new one.</Typography>
      </Box>
    );
  }

  return (
    <Formik
      initialValues={{ password: "", confirmPassword: "" }}
      validate={(values) => {
        const errors = {};
        if (!values.password) errors.password = "*Required";
        else if (values.password.length < 8) errors.password = "Password must be at least 8 characters.";
        if (!values.confirmPassword) errors.confirmPassword = "*Required";
        else if (values.confirmPassword !== values.password) errors.confirmPassword = "Passwords do not match.";
        return errors;
      }}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          const res = await fetch(`${APIURL}/general/reset-password`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email, token, password: values.password }),
          });
          const data = await res.json();
          if (data.error) {
            setSnackbar({ open: true, message: data.error, severity: "error" });
          } else {
            setDone(true);
          }
        } catch {
          setSnackbar({ open: true, message: "An unexpected error occurred. Please try again.", severity: "error" });
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ values, errors, touched, isSubmitting, handleChange, handleBlur, handleSubmit }) => (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
            backgroundImage: "url(https://amldiligence.com/login/client1.jpg)",
            backgroundSize: "cover",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: "60px" }}>
            <img src="./logo.png" alt="AML Diligence" />
            <Typography textAlign="center" fontSize="26px" fontFamily="lucida-console">
              www.AMLdiligence.com
            </Typography>
          </Box>

          <Card sx={{ width: { xs: "90%", sm: 320 } }} elevation={8}>
            <CardContent sx={{ display: "flex", justifyContent: "center" }}>
              {done ? (
                <Box sx={{ p: 2, textAlign: "center" }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>Password Updated</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Your password has been changed successfully. You can now log in with your new password.
                  </Typography>
                  <Button variant="contained" color="success" onClick={() => navigate("/login")}>
                    Go to Login
                  </Button>
                </Box>
              ) : (
                <form onSubmit={handleSubmit} style={{ padding: "10px", display: "flex", flexDirection: "column", alignItems: "center", gap: "14px", width: "100%" }}>
                  <Typography variant="h5">Reset Password</Typography>
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    Resetting password for <strong>{email}</strong>
                  </Typography>

                  <TextField
                    fullWidth
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="New password"
                    value={values.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(errors.password && touched.password)}
                    helperText={touched.password && errors.password}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setShowPassword(v => !v)}>
                            {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    fullWidth
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirm ? "text" : "password"}
                    placeholder="Confirm new password"
                    value={values.confirmPassword}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(errors.confirmPassword && touched.confirmPassword)}
                    helperText={touched.confirmPassword && errors.confirmPassword}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setShowConfirm(v => !v)}>
                            {showConfirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Button fullWidth variant="contained" color="success" type="submit" disabled={isSubmitting}>
                    {isSubmitting ? "Updating…" : "Set New Password"}
                  </Button>

                  <Button fullWidth variant="text" size="small" onClick={() => navigate("/login")}>
                    Back to Login
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>

          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          >
            <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: "100%" }}>
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      )}
    </Formik>
  );
};

export default ResetPassword;
